<script setup lang="ts">
import { getTranslatedProperty } from "@shopware/helpers";
import type { Schemas } from "#shopware";
const { pushSuccess, pushError } = useNotifications();
const {
  setDefaultCustomerShippingAddress,
  setDefaultCustomerBillingAddress,
  deleteCustomerAddress,
  loadCustomerAddresses,
} = useAddress();
const { defaultBillingAddressId, defaultShippingAddressId } = useUser();
const { refreshSessionContext } = useSessionContext();
const { t } = useI18n();

const emits = defineEmits<(e: "success") => void>();

const props = withDefaults(
  defineProps<{
    address: Schemas["CustomerAddress"];
    countries: Array<Schemas["Country"]>;
    salutations: Array<Schemas["Salutation"]>;
    canSetDefault?: boolean;
    canEdit?: boolean;
    canDelete?: boolean;
  }>(),
  {
    canSetDefault: true,
    canEdit: true,
    canDelete: false,
  }
);
const canBeDeleted = computed(
  () =>
    props.canDelete &&
    defaultShippingAddressId.value !== props.address.id &&
    defaultBillingAddressId.value !== props.address.id
);

const setDefaultShippingAddress = async () => {
  try {
    await setDefaultCustomerShippingAddress(props.address.id);
    refreshSessionContext();
    pushSuccess(t("account.messages.defaultShippingAddressSuccess"));
  } catch (error) {
    pushError(t("account.messages.defaultShippingAddressError"));
  }
};

const setDefaultBillingAddress = async () => {
  try {
    await setDefaultCustomerBillingAddress(props.address.id);
    refreshSessionContext();
    pushSuccess(t("account.messages.defaultBillingAddressSuccess"));
  } catch (error) {
    pushError(t("account.messages.defaultBillingAddressError"));
  }
};

const removeAddress = async (addressId: string) => {
  try {
    await deleteCustomerAddress(addressId);
    pushSuccess(t("account.messages.addressDeletedSuccess"));
  } catch (error) {
    pushError(t("account.messages.addressDeletedError"));
  } finally {
    loadCustomerAddresses();
  }
};

const addAddressModalController = useModal();
const addressSaved = () => {
  addAddressModalController.close();
  refreshSessionContext();
  loadCustomerAddresses();
};

const salutationLabel = computed(() => {
  const salutation =
    props.address?.salutation ||
    props.salutations?.find(
      (item: Schemas["Salutation"]) => item.id === props.address?.salutationId
    );

  if (!salutation || salutation.salutationKey === "not_specified") {
    return "";
  }

  return salutation.displayName || salutation.translated?.displayName || "";
});
</script>

<template>
  <div class="">
    <div class="">
      <div v-if="address" class="space-y-1 relative min-w-[200px] max-w-[200px]">
        <div class="card-header relative">
          <div class="flex items-center gap-2 w-[180px]">
            <p>
              <template v-if="salutationLabel">{{ salutationLabel }}&nbsp;</template>{{ address?.firstName }} {{ address?.lastName }}
            </p>
          </div>
          <button
            v-if="canEdit"
            title="Open address editor"
            aria-label="Edit address"
            class="cursor-pointer text-md inline-block absolute right-0 top-0"
            data-testid="address-edit"
            @click.prevent="addAddressModalController.open"
          >
            <i class="text-md justify-center icon-edit" aria-hidden="true" />
          </button>
        </div>
        <div class="address-wrapper">
          <p v-if="address.company || address.department">
            {{ [address.company, address.department].filter(Boolean).join(", ") }}
          </p>
          <p>{{ address.street }}</p>
          <p>{{ address.city }}, {{ address.zipcode }}</p>
          <p>
            {{ getTranslatedProperty(address.country, "name") }}
            <template v-if="address.countryState">
              ,
            </template>
            {{ getTranslatedProperty(address.countryState, "name") }}
          </p>
          <p v-if="address.phoneNumber"  class="mt-6">{{ $t("checkout.telephone") }}  {{ address.phoneNumber }}</p>
          <p v-if="address?.customFields?.custom_address_field_phone_number_2">{{ $t("checkout.telephone") }}  {{ address?.customFields?.custom_address_field_phone_number_2 }}</p>
        </div>
      </div>
      <!-- Continue Button -->

      <SharedModal :controller="addAddressModalController">
        <SharedAccountAddressForm
          :address="address"
          :title="$t('account.editAddress')"
          @success="addressSaved"
        />
      </SharedModal>
    </div>

    <div v-if="canSetDefault">
      <a
        v-if="defaultShippingAddressId !== address.id"
        role="button"
        tabindex="0"
        class="block text-sm mt-4 font-medium text-blue-600 hover:underline"
        data-testid="address-set-default-shipping"
        @click="setDefaultShippingAddress()"
      >
        {{ $t("account.setDefaultShippingAddress") }}
      </a>
      <a
        v-if="defaultBillingAddressId !== address.id"
        role="button"
        tabindex="0"
        class="block text-sm mt-2 font-medium text-blue-600 hover:underline"
        data-testid="address-set-default-billing"
        @click="setDefaultBillingAddress()"
      >
        {{ $t("account.setDefaultBillingAddress") }}
      </a>
    </div>
  </div>
</template>
